
import { Component, Prop, Mixins, Watch } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import { debounce, clone, cloneDeep } from "lodash";

import { FeaturesList, PaginatedSearchQueryParams, UserPermissions, VueBootstrapField, VueBootstrapFieldTableSortEvent } from "@/store/types";
import api from "@/services/api.service";
import CameraSelect from "@/components/form/CameraSelect.vue";
import { truncateString } from "@/filters";
import VuePerfectScrollbar from "vue-perfect-scrollbar";

import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";
import _ from 'lodash';
import PaginatedSearch from "@/mixins/PaginatedSearch";
import AutomationMixin from "@/mixins/AutomationMixin";
import ResponsesPagedResponse from "@/types/sv-data/responses/ResponsesPagedResponse";
import { Response, AlarmGrouping, Script, YesNoOption } from "@/store/responses/types";
import { DeviceDetails, ServerDetails } from '@/store/devices/types';
import vselect3 from "vselect3";
import { validationMixin } from "vuelidate";
import { maxValue, minValue, required, maxLength } from "vuelidate/lib/validators";
import { getDebouncePeriod } from '@/utils/debounce-helper';
import { get } from "lodash";
import ResponseActionSetup from './ResponseActionSetup.vue';
import { ResponseAction } from "@/store/response-actions/types";
import isEqual from 'lodash.isequal';


interface ResponseRow extends Response {
    readOnly?: boolean;
    readOnlyMessage?: string;
    deleteMode?: boolean;
    scriptTitle: string;
    script: Script;
    grouping: AlarmGrouping;
    linkedCameras: DeviceDetails[];
	responseActions?: ResponseAction[];
    maskDurationLimit?: number;
}

interface ServerTypeEvent {
    eventNum: number,
    title: string,
}

interface VueBootstrapFieldEx extends VueBootstrapField {
    keySort?: string;
}

const Devices = namespace("devices");

@Component({
    components: {
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		"v-select-3": vselect3,
		"area-tree-select": AreaTreeSelect,
        "camera-select": CameraSelect,
		ResponseActionSetup
	},
	filters: {
        truncateString
    },
    validations: {
        selectedResponse: {
            serverID: { required, minValue: minValue(1) },
            title: { required, maxLength: maxLength(250) },
            serverTypeEventNum: { required },
            input1: { required, minValue: minValue(-1), maxValue: maxValue(2147483647) },
            input2: { required, minValue: minValue(-1), maxValue: maxValue(2147483647) },
            priority: { minValue: minValue(0), maxValue: maxValue(2147483647) },
            grouping: { required },
            groupID: { required, minValue: minValue(1) },
            maskDurationLimit: { validateRange: function(){
	            if (this.selectedResponse.maskDurationLimit == null || !this.showMaskDurationLimit) {
		            return true;
	            }

	            var _maxValue: number = 2147483647;
	            var _minValue: number = 0;

	            return Number(this.selectedResponse.maskDurationLimit) <= _maxValue && Number(this.selectedResponse.maskDurationLimit) >= _minValue;
            }}
        }
    }
})
export default class DeviceAlarmSetup extends Mixins(PaginatedSearch, validationMixin, AutomationMixin) {
	@Prop({ default: null })
    private selectedServerId: number;

	@Prop({ default: false, type: Boolean })
    private readonly: boolean;

    @Prop({ default: false })
    private inModal: number;

	@Getter("getPermissions")
    private permissions: UserPermissions;
	@Getter("getFeaturesList") featuresList: FeaturesList;

	@Devices.State("serversList")
    private serversList: ServerDetails[];

	private totalRecords: number = 0;
	private isLoading: boolean = false;
	private responses: ResponseRow[] = [];
	private responseScripts: Script[] = [];
    private selectedResponseActions: ResponseAction[] = [];
	private selectedResponse: ResponseRow = null;
	private serverTypeEvents: ServerTypeEvent[] = [];
    private maxNumInputValue: number = 2147483647;
    private recordsPage: number = 1;
	private recordsPageSize: number = 25;
	private recordsPageSizeMax: number = 200;
	private recordsPageSizeUpdated: number = this.recordsPageSize;
	private sortBy: string = "title";
	private sortDesc: boolean = false;
	private searchTerm: string = "";
	private searchTermPersisted: string = "";
    private showModal: boolean = false;
	private updateTable: number = 0;
    private responseActionEdited: boolean = false;

	/**
	*	@returns Table columns
	*/
	private get columns(): VueBootstrapFieldEx[] {
		return [
			{
				label: "Alarm Name",
				key: "title",
				sortable: true,
                keySort: "title",
				visible: true
			},
            {
				label: "Device",
                key: "deviceTitle",
                sortable: true,
                keySort: "Server",
				visible: true
			},
            {
                label: "Area",
                key: "groupTitle",
				sortable: true,
                keySort: "Area",
				visible: true
            },
            {
                label :"Action Plan",
                key:"scriptTitle",
				sortable: true,
                keySort: "ActionPlan",
				visible: true
            },
            {
                label : "Input 1",
                key:"input1",
				sortable: true,
                keySort: "input1",
				visible: true
            },
            {
                label : "Input 2",
                key:"input2",
				sortable: true,
                keySort: "input2",
				visible: true
            },
            {
                label : "Extra Value",
                key:"extraValue",
				sortable: true,
                keySort: "extraValue",
				visible: true
            },
			{
				label: "Priority",
				key: "priority",
				sortable: true,
                keySort: "priority",
				visible: true
			},
            {
                label :"Grouping",
                key:"grouping",
				sortable: true,
                keySort: "grouping",
				visible: true
            },
            {
                label: "Linked Camera",
                key: "linkedCameras",
				sortable: false,
				visible: this.showLinkedCamerasInModal,
            },
            {
                label: "Automations",
                key: "responseActions",
				sortable: false,
				visible: this.showAutomations,
            },
            {
				key: "maskDurationLimit",
				label: "Max Mask Duration",
				sortable: false,
                visible: this.showMaskDurationLimit
			},
			{
				key: "actions",
				label: "Actions",
				sortable: false,
				class: "text-center",
				visible: !this.readonly // ToDo: Is this enough for restricting actions?
			},
		];
	}

    private yesNoNotSet: YesNoOption[] = [
        {
            title: "Yes",
            value: true
        },
        {
            title: "No",
            value: false
        },
        {
            title: "Not set",
            value: null
        }
    ];

    private alarmGroupingOptions: AlarmGrouping[] = [
        {
            title: "By Area",
            isRaiseIndividual: false,
            isRaiseGrouped: false
        },
        {
            title: "By Alarm",
            isRaiseIndividual: false,
            isRaiseGrouped: true
        },
        {
            title: "Not Grouped",
            isRaiseIndividual: true,
            isRaiseGrouped: false
        },
        {
            title: "Not Set",
            isRaiseIndividual: null,
            isRaiseGrouped: null
        }
    ];

    private get responseInvalid(): boolean {
        let invalid = this.$v.selectedResponse.$invalid
        if (invalid) {
            return invalid
        }

        if (!this.selectedResponse)
        {
            return false;
        }

        const storedResponse = this.responses.find(r => r.responseID == this.selectedResponse.responseID)
        if (!storedResponse)
        {
            return false;
        }

        return (
            this.selectedResponse &&
            this.selectedResponse.title == storedResponse.title &&
            this.selectedResponse.serverID == storedResponse.serverID &&
            this.selectedResponse.input1 == storedResponse.input1 &&
            this.selectedResponse.extraValue  == storedResponse.extraValue &&
            this.selectedResponse.input2 == storedResponse.input2 &&
            this.selectedResponse.serverTypeEventNum == storedResponse.serverTypeEventNum &&
            this.selectedResponse.script === storedResponse.script &&
            this.selectedResponse.priority == storedResponse.priority &&
            isEqual(this.selectedResponse.grouping, storedResponse.grouping) &&
            this.selectedResponse.autoHandle === storedResponse.autoHandle &&
            isEqual(this.selectedResponse.linkedCameras,storedResponse.linkedCameras) &&
            this.selectedResponse.groupID == storedResponse.groupID &&
            this.selectedResponse.maskDurationLimit == storedResponse.maskDurationLimit &&
            this.responseActionEdited == false
        );
    }

    private get deleteMode(): boolean
    {
        return this.selectedResponse && this.selectedResponse.deleteMode;
    }

    private get responseTitle(): string
    {
        if (!this.selectedResponse || !this.selectedResponse.responseID)
        {
            return "";
        }

        return this.responses.find(r => r.responseID == this.selectedResponse.responseID)?.title;
    }

    private get modalTitle(): string
    {
        return (this.selectedResponse && this.selectedResponse.responseID) ? `Editing Alarm "${this.responseTitle}"` : "Create New Alarm"
    }

	private get showAutomations(): boolean {
		return  !!get(this.featuresList, ["Alarms", "CameraAutomation"]) &&
		(!!this.permissions.canViewAutomations || !!this.permissions.canEditAutomations);
	}

	private get showLinkedCamerasInModal(): boolean {
		return !!get(this.featuresList, ["Devices"]) && !get(this.featuresList, ["Alarms", "CameraAutomation"]);
	}

	private get showMaskDurationLimit(): boolean {
		return get(this.featuresList, ["Alarms", "Masking", "MaxMaskDuration"], false);
	}

	private async mounted(): Promise<void>
    {
		this.responseScripts = await api.getResponseScripts();
        if (!this.readonly)
        {
            this.addActions();
        }
		await this.updateResponses();
	}

    private addActions(): void
    {
		this.columns.push({ key: "actions", label: "Actions", sortable: false, class: "text-center" });
	}

    private async putAlarm(): Promise<void>
    {
        var response = {...this.selectedResponse};
        if (response.script)
        {
            response.scriptID = response.script.scriptID;
            response.scriptTitle = response.script.title;
        }
        else
        {
            response.scriptID = null;
            response.scriptTitle = null;
        }

        if (!response.serverID)
        {
            response.serverID = this.selectedServerId;
        }

        response.isRaiseIndividual = response.grouping.isRaiseIndividual;
        response.isRaiseGrouped = response.grouping.isRaiseGrouped;

        var result = null;
        if (!response.responseID)
        {
            result = await api.createDeviceResponse(response);
        }
        else
        {
            result = await api.updateResponse(response);
        }

        if (!result || !result.data)
        {
            return;
        }

        this.selectedResponse = result.data;
        await this.updateResponseActions(response.responseID, response.responseActions, this.selectedResponseActions);
        await this.serverIdWatch(this.selectedResponse.serverID);
        await this.updateResponses();
        this.showModal = false;
        this.selectedResponseActions = [];
        this.selectedResponse = null;
	}

    private async updateResponseActions(responseId: number, newResponseActions: ResponseAction[], oldResponseActions:  ResponseAction[]){
		// Cant use foreach loop as it does not play nicely with async calls
		// Create/Update responseActions
		if(newResponseActions != null)
		{
			for(let i = 0; i < newResponseActions.length; i++ ) {
				try {
					var newAutomation = newResponseActions[i];
                    newAutomation.description = null;


					// If we have no response its a new automation
					if(!newAutomation.responseActionID)
					{
						newAutomation.responseID = responseId;

						await api.createAlarmAutomation(responseId, newAutomation);
					}
					else{

						// Check to see if changees have been made to an item before we try and hit the backend
						// Shallow compare
						var oldAutomation = oldResponseActions.find(r => r.responseActionID == newResponseActions[i].responseActionID)

						var updated = newAutomation.objectID != oldAutomation.objectID
									|| newAutomation.responseActionTypeID != oldAutomation.responseActionTypeID
									|| newAutomation.settingsMeta != oldAutomation.settingsMeta
						if(updated)
						{
							await api.updateAlarmAutomation(responseId, newAutomation);
						}
					}
				}
				catch (error) {
					this.$notify({
					type: "error",
					title: "Failed to create/update automation",
					text: error.response.data
					});
				}
			}
		}
		// Delete responseActions that no longer exist
		if(oldResponseActions  && oldResponseActions.length > 0)
		{
			for(let i = 0; i < oldResponseActions.length ; i++)
			{
				if(!newResponseActions.find(r => r.responseActionID == oldResponseActions[i].responseActionID))
				{
					try {
                        await api.deleteAlarmAutomation(this.selectedResponse.responseID, oldResponseActions[i].responseActionID)
                    }
                    catch (error) {
                        this.$notify({
                            type: "error",
                            title: "Failed to delete automation",
                            text: error.response.data
					    });
				    }
				}
			}
		}
	}

    private async deleteAlarm(): Promise<void>
    {
        if (this.selectedResponse && this.selectedResponse.responseID)
        {
            await api.deleteResponse(this.selectedResponse.responseID);
            await this.updateResponses();
            this.selectedResponse = null;
            this.showModal = false;
        }
	}

    private async loadServerTypes(serverId: number): Promise<void>
    {
        if (!serverId)
        {
            this.serverTypeEvents = [];
            return;
        }

        const selectedServer = this.serversList.find(s => s.serverID == serverId);
        if (selectedServer)
        {
            this.serverTypeEvents = (await api.loadServerEventTypes(selectedServer.serverTypeID, null)).data;
        }
        else
        {
            this.serverTypeEvents = [];
        }

        if (!this.serverTypeEvents)
        {
            this.serverTypeEvents = []
        }

        this.serverTypeEvents.unshift({eventNum: -1, title: "Default"});
    }

    private handleSearchInput = debounce(async (searchTerm: string): Promise<void> => {
		await this.performSearch(searchTerm);
	}, getDebouncePeriod());

	private async performSearch(searchTerm: string): Promise<void> {
		this.searchTerm = searchTerm;
		this.recordsPage = 1;
		await this.updateResponses();
	}

    @Watch("selectedServerId")
	public async updateResponses(): Promise<void> {
		try
        {
			this.isLoading = true;
            await this.loadServerTypes(this.selectedServerId);
            const sortBy = this.columns.find(f => f.key == this.sortBy)?.keySort;

            const queryParams: PaginatedSearchQueryParams = {
                page: this.recordsPage,
                pageSize: this.recordsPageSize,
                sortBy: sortBy,
                sortDesc: this.sortDesc,
                searchTerm: this.searchTerm,
            };

            const cancellableQuery = this.generateNewPaginatedSearchRequest(queryParams);

			if (this.selectedServerId)
            {
				cancellableQuery.params.serverId = this.selectedServerId;
			}

            cancellableQuery.params.isEmailAlarms = false;


			let result: ResponsesPagedResponse = await api.getResponses(cancellableQuery);
			var responseData: ResponseRow[] = result.data as ResponseRow[];

			if (responseData) {
				responseData = responseData.map(response => {
					response.readOnly = !!response.groupSyncId && !this.permissions.canOverrideGroupSync && !this.permissions.isSystemAdmin;
                    response.deleteMode = false;
					return response;
				});
			}

			this.responses = responseData;
            if (this.responses)
            {
                for(var i = 0; i < this.responses.length; i++)
                {
                    var response = this.responses[i];
                    response.script = this.responseScripts.find(rs => rs.scriptID === response.scriptID);
                    response.grouping = this.alarmGroupingOptions.find(ag =>
                        ag.isRaiseGrouped == response.isRaiseGrouped &&
                        ag.isRaiseIndividual == response.isRaiseIndividual);

                    if (this.showAutomations) {
                        await api.getAlarmAutomations(response.responseID).then(res => {
                            response.responseActions = res;
                        });
                    }
                }
                this.updateTable++;
            }

			this.totalRecords = result.totalRecords;
		}
		catch (ex)
        {
			console.error("Unexpected error searching alarms: " + ex);
		}
        finally
        {
			this.isLoading = false;
		}
    }

    private async onRecordsPerPageClick() {
		let pageSize = +this.recordsPageSizeUpdated;

		if (pageSize)
        {
			if (pageSize > this.recordsPageSizeMax)
            {
				pageSize = this.recordsPageSizeMax;
			}

			if (pageSize !== this.recordsPageSize)
            {
				this.recordsPage = 1;
			}

			this.recordsPageSize = pageSize;
		}
		this.recordsPageSizeUpdated = this.recordsPageSize;
		this.onPageClick(this.recordsPage);
	}

    private async onPageClick(page: number) {
		if (this.isLoading)
        {
			return;
		}

		this.recordsPage = page || this.recordsPage;

		await this.updateResponses();
	}

    private async onSortChange(event: VueBootstrapFieldTableSortEvent): Promise<void> {
		if (this.isLoading)
        {
			return;
		}

		this.sortBy = event.sortBy || this.sortBy;
		this.sortDesc = event.sortDesc;

		await this.updateResponses();
	}

    private async openModal(response: ResponseRow, deleteMode: boolean = false): Promise<void>
    {
        this.selectedResponseActions = []

        if (response)
        {
            this.selectedResponse = cloneDeep(response);
            this.selectedResponse.deleteMode = deleteMode;

            if(this.selectedResponse.responseActions)
            {
                this.selectedResponseActions = cloneDeep(this.selectedResponse.responseActions)
            }
        }
        else
        {
            this.selectedResponse = {
                responseID: null,
                groupID: null,
                serverTypeEventNum: -1,
                title: null,
                script: null,
                input1: null,
                input2: -1,
                extraValue: null,
                priority: null,
                grouping: null,
                isRaiseIndividual: null,
                isRaiseGrouped: null,
                linkedCameras: [],
                autoHandle: null,
                deleteMode: false,
				responseActions: [],
                maskDurationLimit: null
            } as ResponseRow

            if (this.selectedServerId)
            {
                this.selectedResponse.serverID = this.selectedServerId;
            }
        }

        await this.loadServerTypes(this.selectedResponse.serverID);
        this.responseActionEdited = false;
        this.showModal = true;
    }

    private handleModalHidden(): void
    {
        this.selectedResponse = null;
        this.selectedResponseActions = [];
        this.showModal = false;
    }

    @Watch("selectedResponse.serverID", { deep:true })
    private async serverIdWatch(newServerId: number | null): Promise<void>
    {
        if (!newServerId)
        {
            this.serverTypeEvents = [];
            if (this.selectedResponse)
            {
                this.selectedResponse.serverTypeEventNum = null;
            }
            return;
        }

        await this.loadServerTypes(newServerId);
        if (this.selectedResponse)
        {
            this.selectedResponse.serverTypeEventNum = -1;
            if(!this.selectedResponse.responseID)
            {
                const server = this.serversList.find(sl => sl.serverID == newServerId);
                this.selectedResponse.groupID = server?.groupID;
            }
        }
    }

	private get visibleFields(): VueBootstrapFieldEx[] {
		return this.columns.filter(c => c.visible);
	}

    private responseActionInput(input:  ResponseAction[]): void {
        this.selectedResponse.responseActions = input;
        this.responseActionEdited = true;
    }
}
